import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from "react-router-dom";
import axios from 'axios';
import { Accordion } from 'react-bootstrap';
import {
  AppCard,
  AppDeleteModal,
  AppModal,
  AppNoInfo,
  AppRemixIcon,
} from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import * as moduleAction from '../../../store/actions/moduleAction';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath } from '../../../routing/helpers/router';
import AppLink from '../../../routing/helpers/AppLink';
import Translator from '../../../layout/components/helpers/Translator';
import Sidebar from '../../../layout/Sidebar/Sidebar';
import InternalObligationSidebarBlock from '../../../layout/screenSpecifics/Modules/LegalCompliance/InternalObligation/InternalObligationSidebarBlock';
import colors from '../../../constants/colors';
import InternalObligationForm from '../../../layout/screenSpecifics/Modules/LegalCompliance/InternalObligation/InternalObligationForm';

export default function LegalComplianceInternalObligationScreen() {
  const { clientID, moduleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const client = useSelector((state) => state.auth.user.client);
  const [isLoading, setIsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState();
  const [obligationAndTemplates, setObligationAndTemplates] = useState();

  // < STORE CALLS
  const loadModulePermissions = useCallback(async () => {
    try {
      await dispatch(
        moduleAction.fetch(
          axiosPath(
            "api_r_user_module_legalCompliance_evaluation_getPermissions"
          ),
          "permissions",
          {
            moduleBridgeHex,
            plantHex,
            subType: "permissions",
          }
        )
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath(
            "api_r_user_module_legalCompliance_evaluation_getPermissions"
          ),
          "userBridge",
          {
            moduleBridgeHex,
            plantHex,
            subType: "userBridge",
          }
        )
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath(
            "api_r_user_module_legalCompliance_evaluation_getPermissions"
          ),
          "jsonSettings",
          {
            moduleBridgeHex,
            plantHex,
            subType: "jsonSettings",
          }
        )
      );
    } catch (err) {}
  }, [moduleBridgeHex, plantHex]);

  useEffect(() => {
    loadModulePermissions();
  }, [moduleBridgeHex, plantHex]);
  // < STORE CALLS

  // > LOAD INTERNAL OBLIGATIONS
  const loadInternalObligations = useCallback(async () => {
    setIsLoading(true);

    return axios
      .post(
        axiosPath("api_r_user_module_legalCompliance_internalObligation_list"),
        {
          moduleBridgeHex,
          plantHex,
        }
      )
      .then(({ data }) => {
        if (isMounted.current) setObligationAndTemplates(data);
      })
      .catch((e) => dispatch(errorHandler(e)))
      .finally(() => setIsLoading(false));
  }, [moduleBridgeHex, plantHex]);

  useEffect(() => {
    loadInternalObligations();
  }, []);
  // < LOAD INTERNAL OBLIGATIONS

  const handleDelete = useCallback(
    () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_legalCompliance_internalObligation_delete"
          ),
          {
            moduleBridgeHex,
            plantHex,
            internalObligationHex: showDeleteModal,
          }
        )
        .then(() => {
          loadInternalObligations();
          setShowDeleteModal();
        })
        .catch((e) => dispatch(errorHandler(e, true))),
    [moduleBridgeHex, plantHex, showDeleteModal]
  );

  return (
    <>
      <BreadcrumbHandler
        paths={[
          {
            name: ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="internal-obligation"
              />,
            ),
          },
        ]}
      />
      <Sidebar>
        <img
          src={client?.logoUrl}
          className="img-fluid"
          alt={client?.name}
          title={client?.name}
        />
        <Accordion>
          <ul>
            <li>
              <AppLink
                path="user_module_legalCompliance_evaluation"
                params={{
                  clientID,
                  moduleBridgeHex,
                  plantHex,
                  page: 1,
                  filtersHex: 0,
                }}
                className="sidebar-element"
              >
                <AppRemixIcon
                  icon="arrow-go-back-line"
                  className="sidebar-icon"
                  noVariants
                />{' '}
                <span className="sb-title">
                  <Translator file="module-legalCompliance" name="go.back" />
                </span>
              </AppLink>
            </li>
          </ul>
        </Accordion>
      </Sidebar>
      <main role="main" className="col-md-12 col-lg-12 px-md-4">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0 border-bottom">
          <section className="content">
            <AppCard>
              <InternalObligationSidebarBlock
                internalObligationTemplates={
                  obligationAndTemplates?.internalObligationTemplates
                }
                onReload={loadInternalObligations}
              />
              {!isLoading ? (
                <div className="mt-3">
                  {obligationAndTemplates?.obligations.length > 0 ? (
                    <>
                      <table className="table table-hover table-sm">
                        <thead>
                          <tr>
                            <th className="col-1">CODE</th>
                            <th className="col-4">
                              <Translator
                                file="module-legalCompliance"
                                name="obligation.name"
                              />
                            </th>
                            <th className="col-3">
                              <Translator
                                file="module-legalCompliance"
                                name="module.lc.evidence"
                              />
                            </th>
                            <th className="col-3">
                              <Translator
                                file="module-legalCompliance"
                                name="module.lc.nextDate"
                              />
                            </th>
                            <th className="col-1">
                              <Translator
                                file="module-legalCompliance"
                                name="options"
                              />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {obligationAndTemplates?.obligations.map(
                            (obligation) => (
                              <tr key={obligation.hex}>
                                <td>{obligation.hex}</td>
                                <td
                                  className="text-wrap"
                                  style={{
                                    whiteSpace: 'normal',
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word',
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: obligation.obligation,
                                  }}
                                />

                                <td
                                  className="text-wrap"
                                  style={{
                                    whiteSpace: 'normal',
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word',
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: obligation.evidence,
                                  }}
                                />

                                <td
                                  className="text-wrap"
                                  style={{
                                    whiteSpace: 'normal',
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word',
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: obligation.nextDate,
                                  }}
                                />
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-warning"
                                    onClick={() => setShowEditModal(obligation)}
                                  >
                                    <AppRemixIcon icon="edit" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger ml-2"
                                    onClick={() =>
                                      setShowDeleteModal(obligation?.hex)
                                    }
                                  >
                                    <AppRemixIcon icon="delete-bin-6" />
                                  </button>
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                      <AppModal
                        show={showEditModal !== undefined}
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-legalCompliance"
                            name="internal-obligation"
                          />,
                        )}
                        onClose={() => setShowEditModal(undefined)}
                        size="xl"
                      >
                        <InternalObligationForm
                          internalObligation={showEditModal}
                          onReload={() => {
                            loadInternalObligations();
                            setShowEditModal(undefined);
                          }}
                        />
                      </AppModal>
                      <AppDeleteModal
                        show={showDeleteModal}
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="admin"
                            name="general.delete-folder"
                          />,
                        )}
                        onClose={() => setShowDeleteModal(undefined)}
                        onConfirm={handleDelete}
                        sensitive
                      />
                    </>
                  ) : (
                    <AppNoInfo
                      text="No existen obligaciones internas"
                      icon={
                        <AppRemixIcon
                          icon="file-unknow"
                          color={colors.grey500}
                          size={44}
                        />
                      }
                      minHeight={250}
                    />
                  )}
                </div>
              ) : (
                <AppNoInfo text="Cargando..." minHeight={250} />
              )}
            </AppCard>
          </section>
        </div>
      </main>
    </>
  );
}
